/**
 * Compact verse selection serialization for reader URL
 * (`?sel=`). Makes **active selection shareable/reloadable**: pasting URL in
 * another browser reproduces selected verses exactly.
 *
 * Format: comma-separated list of tokens, each token a number
 * suelto (`9`) o un rango inclusivo `inicio-fin` (`4-6`). Ejemplo: `4-6,9`.
 * Serializer collapses consecutive numbers into ranges for compact URLs.
 */

/** Parses `?sel=` into sanitized, sorted list without duplicates. */
export function parseVerseSelection(value: unknown): number[] {
  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }
  const out = new Set<number>();
  for (const rawToken of value.split(",")) {
    const token = rawToken.trim();
    if (token === "") {
      continue;
    }
    const rangeMatch = /^(\d+)-(\d+)$/.exec(token);
    if (rangeMatch) {
      let start = Number.parseInt(rangeMatch[1] ?? "", 10);
      let end = Number.parseInt(rangeMatch[2] ?? "", 10);
      if (!Number.isInteger(start) || !Number.isInteger(end)) {
        continue;
      }
      if (start > end) {
        [start, end] = [end, start];
      }
      // Defensive boundary: never expand an absurd range from URL.
      const MAX_SPAN = 500;
      if (start <= 0 || end - start > MAX_SPAN) {
        continue;
      }
      for (let v = start; v <= end; v += 1) {
        out.add(v);
      }
    } else {
      const n = Number.parseInt(token, 10);
      if (Number.isInteger(n) && n > 0) {
        out.add(n);
      }
    }
  }
  return [...out].toSorted((a, b) => a - b);
}

/**
 * Serializes verse list to compact `4-6,9` format. Assumes arbitrary
 * input: sorts, dedupes, and collapses consecutive ranges. Empty string if
 * no selection (omits param for clean URL).
 */
export function serializeVerseSelection(verses: number[]): string {
  const sorted = [...new Set(verses.filter((v) => Number.isInteger(v) && v > 0))].toSorted(
    (a, b) => a - b,
  );
  const first = sorted[0];
  if (first === undefined) {
    return "";
  }
  const tokens: string[] = [];
  let rangeStart = first;
  let prev = rangeStart;
  for (let i = 1; i <= sorted.length; i += 1) {
    const current = sorted[i];
    if (current !== undefined && current === prev + 1) {
      prev = current;
      continue;
    }
    tokens.push(rangeStart === prev ? String(rangeStart) : `${rangeStart}-${prev}`);
    if (current !== undefined) {
      rangeStart = current;
      prev = current;
    }
  }
  return tokens.join(",");
}

/**
 * READABLE label of same selection — `"3-5, 9"` — for header of
 * highlight sheet (`selectedVersesFormat` from published app). Separated from
 * `serializeVerseSelection` only differs by space after comma: URL wants
 * compact, eye wants breathing room. Range compaction is identical,
 * maintaining single rule.
 */
export function formatVerseSelectionLabel(verses: number[]): string {
  return serializeVerseSelection(verses).split(",").join(", ");
}
